// Notifications page. Loads the outbox once, then keeps it fresh off the
// notification.changed SSE — mirroring PaymentsPage's load / live-refresh / queued-mutate shape.
// "Process pending" drains the outbox worker; per-row Retry / Cancel post the corresponding
// outbox transitions (all reconcile on the SSE refresh).
import { useCallback, useEffect, useState } from 'react';
import {
  type NotificationDto,
  type NotificationStatus,
  formatDateTime,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card, PageHeader, Pill } from '../../ui/primitives.tsx';
import { NotificationSettings } from '../../ui/NotificationSettings.tsx';

type Tone = 'neutral' | 'good' | 'bad';

function statusTone(status: NotificationStatus): Tone {
  if (status === 'sent') return 'good';
  if (status === 'failed') return 'bad';
  return 'neutral';
}

function statusLabel(status: NotificationStatus): string {
  return t(`notifications.status.${status}`);
}

export function NotificationsPage() {
  const can = useCan();
  const manage = can('notification:manage');
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<NotificationDto[]>('/notifications')
      .then(setNotifications)
      .catch(() => setError(t('notifications.loadError')));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A notification was enqueued, processed, retried or cancelled somewhere (our own queued
  // replay or another user / the worker) — pull fresh truth so every open tab reconciles.
  useLiveEvent('notification.changed', () => {
    load();
  });

  async function processPending() {
    setError(null);
    try {
      await api.post('/notifications/process', { queue: true });
      load();
    } catch {
      setError(t('notifications.processError'));
      load();
    }
  }

  async function retry(notification: NotificationDto) {
    setError(null);
    try {
      await api.post(`/notifications/${notification.id}/retry`, {}, { queue: true });
      load();
    } catch {
      setError(t('notifications.retryError'));
      load();
    }
  }

  async function cancel(notification: NotificationDto) {
    setError(null);
    try {
      await api.post(`/notifications/${notification.id}/cancel`, {}, { queue: true });
      load();
    } catch {
      setError(t('notifications.cancelError'));
      load();
    }
  }

  return (
    <div>
      <PageHeader title={t('notifications.title')} subtitle={t('notifications.subtitle')} />

      <div className="mb-4 max-w-2xl">
        <NotificationSettings />
      </div>

      {manage && (
        <div className="mb-4 flex items-center gap-2">
          <Button className="ml-auto" onClick={processPending}>
            {t('notifications.process')}
          </Button>
        </div>
      )}

      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('notifications.col.created')}</th>
              <th className="px-4 py-3 font-medium">{t('notifications.col.channel')}</th>
              <th className="px-4 py-3 font-medium">{t('notifications.col.recipient')}</th>
              <th className="px-4 py-3 font-medium">{t('notifications.col.template')}</th>
              <th className="px-4 py-3 font-medium">{t('notifications.col.status')}</th>
              <th className="px-4 py-3 font-medium">{t('notifications.col.attempts')}</th>
              {manage && (
                <th className="px-4 py-3 font-medium">{t('notifications.col.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 && (
              <tr>
                <td colSpan={manage ? 7 : 6} className="px-4 py-6 text-center text-muted">
                  {t('notifications.empty')}
                </td>
              </tr>
            )}
            {notifications.map((n) => (
              <tr key={n.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2 font-mono text-xs">{formatDateTime(n.createdAt)}</td>
                <td className="px-4 py-2">{n.channel}</td>
                <td className="px-4 py-2 font-mono text-xs">{n.recipient}</td>
                <td className="px-4 py-2">{n.template}</td>
                <td className="px-4 py-2">
                  <Pill tone={statusTone(n.status)}>{statusLabel(n.status)}</Pill>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{n.attempts}</td>
                {manage && (
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      {(n.status === 'failed' || n.status === 'pending') && (
                        <Button variant="ghost" onClick={() => retry(n)}>
                          {t('notifications.retry')}
                        </Button>
                      )}
                      {n.status === 'pending' && (
                        <Button variant="ghost" onClick={() => cancel(n)}>
                          {t('notifications.cancel')}
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
